import React, { useState, useEffect } from 'react';
import { API_URL, formatMoney } from '../../utils/constants';
import { styles, initStyles } from './styles';
import InfoTab from './InfoTab';
import PaymentTab from './PaymentTab';
import HistoryTab from './HistoryTab';
import CustomerTab from './CustomerTab';

// 스타일 초기화
initStyles();

function EditRecordModal({ record, token, onClose }) {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    customerId: record.customer_id,
    companyName: record.company_name,
    manager: record.manager,
    phone: record.phone,
    mobile: record.mobile,
    address: record.address,
    // 세금계산서용 새 필드
    businessNumber: record.business_number || '',
    representative: record.representative || '',
    businessType: record.business_type || '',
    businessCategory: record.business_category || '',
    email: record.email || '',
    // 작업 정보
    receiptDate: record.receipt_date ? record.receipt_date.split('T')[0] : '',
    receiptDetails: record.receipt_details || '',
    processDetails: record.process_details || '',
    completionDate: record.completion_date ? record.completion_date.split('T')[0] : '',
    laborCost: record.labor_cost ? String(Math.floor(parseFloat(record.labor_cost))) : '',
    paymentMethod: record.payment_method || '',
    progressStatus: record.progress_status || '대기',
    notes: record.notes || '',
    taxInvoice: record.tax_invoice || '',
    vatIncluded: record.vat_included !== false
  });
  
  const [payments, setPayments] = useState([]);
  const [newPayment, setNewPayment] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: '',
    taxInvoice: ''
  });
  const [history, setHistory] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  
  // 사용 자재 관련 상태
  const [usedMaterials, setUsedMaterials] = useState([]);
  const [customMaterialCost, setCustomMaterialCost] = useState(null);
  const [materialCategory, setMaterialCategory] = useState('');
  const [materialSearchTerm, setMaterialSearchTerm] = useState('');
  const [materialSearchResults, setMaterialSearchResults] = useState([]);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(-1);
  const [materialQuantity, setMaterialQuantity] = useState(1);
  const [isAddingMaterial, setIsAddingMaterial] = useState(false);
  const [initialCostCheckDone, setInitialCostCheckDone] = useState(false);

  useEffect(() => {
    fetchUsedMaterials();
  }, []);

  useEffect(() => {
    if (activeTab === 'payment') {
      fetchPayments();
    } else if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    const loadPaymentTaxInvoice = async () => {
      try {
        const response = await fetch(`${API_URL}/payments/${record.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const paymentsData = await response.json();
          if (paymentsData.length > 0) {
            setFormData(prev => ({
              ...prev,
              taxInvoice: paymentsData[0].tax_invoice || ''
            }));
          }
        }
      } catch (error) {
        console.error('결제 정보 조회 오류:', error);
      }
    };
    
    loadPaymentTaxInvoice();
  }, [record.id, token]);

  const fetchPayments = async () => {
    try {
      const response = await fetch(`${API_URL}/payments/${record.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setPayments(await response.json());
      }
    } catch (error) {
      console.error('결제 내역 조회 오류:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/record-history/${record.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setHistory(await response.json());
      }
    } catch (error) {
      console.error('이력 조회 오류:', error);
    }
  };

  const fetchUsedMaterials = async () => {
    try {
      const response = await fetch(`${API_URL}/materials/usage/${record.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsedMaterials(data);
        
        // 초기 로딩 시 저장된 total_cost와 계산된 자재비 비교
        if (!initialCostCheckDone) {
          const calculatedSum = data.reduce((sum, m) => 
            sum + (parseFloat(m.unit_price || 0) * (m.quantity || 1)), 0
          );
          
          const savedLabor = parseFloat(record.labor_cost || 0);
          const savedTotal = parseFloat(record.total_cost || 0);
          
          if (savedTotal > 0) {
            const savedMaterialCost = savedTotal - savedLabor;
            // 자재 합계와 저장된 부품비용이 다르면(수동 수정됨) customMaterialCost 설정
            // 부동소수점 오차 고려하여 1원 이상 차이나면 설정
            if (Math.abs(savedMaterialCost - calculatedSum) > 1) {
              setCustomMaterialCost(savedMaterialCost);
            }
          }
          setInitialCostCheckDone(true);
        }
      }
    } catch (error) {
      console.error('사용 자재 조회 오류:', error);
    }
  };

  const searchAvailableMaterials = async (category, term) => {
    if (!category) {
      setMaterialSearchResults([]);
      return;
    }

    try {
      const params = new URLSearchParams({ category });
      if (term) params.append('term', term);
      
      const response = await fetch(`${API_URL}/materials/available?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMaterialSearchResults(await response.json());
        setSelectedMaterialIndex(-1);
      }
    } catch (error) {
      console.error('자재 검색 오류:', error);
    }
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setMaterialCategory(category);
    setMaterialSearchTerm('');
    setMaterialSearchResults([]);
    if (category) {
      searchAvailableMaterials(category, '');
    }
  };

  const handleMaterialSearchChange = (e) => {
    const term = e.target.value;
    setMaterialSearchTerm(term);
    searchAvailableMaterials(materialCategory, term);
  };

  const handleMaterialKeyDown = (e) => {
    if (!materialSearchResults.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMaterialIndex(prev => 
        prev < materialSearchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMaterialIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedMaterialIndex >= 0) {
        handleSelectMaterial(materialSearchResults[selectedMaterialIndex]);
      }
    }
  };

  const handleSelectMaterial = async (material) => {
    if (isAddingMaterial) return;
    
    const qty = parseInt(materialQuantity) || 1;
    if (qty > material.total_quantity) {
      alert(`재고가 부족합니다. (현재 재고: ${material.total_quantity}개)`);
      return;
    }

    setIsAddingMaterial(true);
    try {
      const response = await fetch(`${API_URL}/materials/use`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          record_id: record.id,
          category: material.category,
          product_name: material.product_name,
          quantity: qty,
          recipient: formData.companyName
        })
      });

      if (response.ok) {
  setMaterialCategory('');  // 항목 선택 초기화
  setMaterialSearchTerm('');
  setMaterialSearchResults([]);
  setSelectedMaterialIndex(-1);
  setMaterialQuantity(1);
  fetchUsedMaterials();
      } else {
        const error = await response.json();
        alert(error.error || '자재 추가 실패');
      }
    } catch (error) {
      console.error('자재 추가 오류:', error);
      alert('자재 추가 중 오류가 발생했습니다.');
    } finally {
      setIsAddingMaterial(false);
    }
  };

  const handleDeleteMaterial = async (usageId) => {
    if (!window.confirm('이 자재 사용을 취소하시겠습니까? (재고가 복구됩니다)')) return;

    try {
      const response = await fetch(`${API_URL}/materials/usage/${usageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchUsedMaterials();
        if (materialCategory) {
          searchAvailableMaterials(materialCategory, materialSearchTerm);
        }
      }
    } catch (error) {
      console.error('자재 삭제 오류:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone' || name === 'mobile') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      
      if (name === 'mobile' && cleaned.startsWith('01')) {
        if (cleaned.length <= 3) {
          formatted = cleaned;
        } else if (cleaned.length <= 7) {
          formatted = `${cleaned.slice(0,3)}-${cleaned.slice(3)}`;
        } else if (cleaned.length <= 11) {
          formatted = `${cleaned.slice(0,3)}-${cleaned.slice(3,7)}-${cleaned.slice(7,11)}`;
        }
      } else if (name === 'phone') {
        if (cleaned.startsWith('02')) {
          if (cleaned.length <= 2) {
            formatted = cleaned;
          } else if (cleaned.length <= 5) {
            formatted = `${cleaned.slice(0,2)}-${cleaned.slice(2)}`;
          } else if (cleaned.length <= 9) {
            formatted = `${cleaned.slice(0,2)}-${cleaned.slice(2,5)}-${cleaned.slice(5,9)}`;
          } else if (cleaned.length <= 10) {
            formatted = `${cleaned.slice(0,2)}-${cleaned.slice(2,6)}-${cleaned.slice(6,10)}`;
          }
        } else if (cleaned.startsWith('0')) {
          if (cleaned.length <= 3) {
            formatted = cleaned;
          } else if (cleaned.length <= 6) {
            formatted = `${cleaned.slice(0,3)}-${cleaned.slice(3)}`;
          } else if (cleaned.length <= 10) {
            formatted = `${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6,10)}`;
          } else if (cleaned.length <= 11) {
            formatted = `${cleaned.slice(0,3)}-${cleaned.slice(3,7)}-${cleaned.slice(7,11)}`;
          }
        }
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else if (name === 'laborCost') {
      setFormData(prev => ({ ...prev, [name]: value.replace(/[^\d]/g, '') }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const recordResponse = await fetch(`${API_URL}/records/${record.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerId: formData.customerId,
          receiptDate: formData.receiptDate,
          receiptDetails: formData.receiptDetails,
          processDetails: formData.processDetails,
          completionDate: formData.completionDate || null,
          laborCost: formData.laborCost || null,
          customMaterialCost: customMaterialCost,
          paymentMethod: formData.paymentMethod,
          progressStatus: formData.progressStatus,
          notes: formData.notes,
          vatIncluded: formData.vatIncluded
        })
      });

      if (recordResponse.ok) {
        setTimeout(() => {
          setIsSaving(false);
          onClose();
        }, 100);
      } else {
        setIsSaving(false);
      }
    } catch (error) {
      console.error('수정 오류:', error);
      setIsSaving(false);
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || !newPayment.paymentDate) {
      return;
    }

    setIsAddingPayment(true);
    try {
      const response = await fetch(`${API_URL}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recordId: record.id,
          paymentDate: newPayment.paymentDate,
          amount: newPayment.amount,
          paymentMethod: newPayment.paymentMethod,
          taxInvoice: newPayment.taxInvoice
        })
      });

      if (response.ok) {
        setNewPayment({
          paymentDate: new Date().toISOString().split('T')[0],
          amount: '',
          paymentMethod: '',
          taxInvoice: ''
        });
        fetchPayments();
        setTimeout(() => setIsAddingPayment(false), 100);
      } else {
        setIsAddingPayment(false);
      }
    } catch (error) {
      console.error('결제 추가 오류:', error);
      setIsAddingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`${API_URL}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPayments();
      }
    } catch (error) {
      console.error('결제 삭제 오류:', error);
    }
  };

  // 자재 총액 계산
  const totalMaterialCost = usedMaterials.reduce((sum, m) => 
    sum + (parseFloat(m.unit_price || 0) * (m.quantity || 1)), 0
  );
  
  // 실제 적용될 부품비용
  const effectiveMaterialCost = customMaterialCost !== null ? customMaterialCost : totalMaterialCost;
  
  const tabs = [
    { id: 'info', label: '작업 정보', icon: '📋' },
    { id: 'payment', label: '결제 내역', icon: '💳' },
    { id: 'customer', label: '고객 정보', icon: '🏢' },
    { id: 'history', label: '수정 이력', icon: '📜' }
  ];

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>작업 수정 (접수번호: {record.id})</h2>
          <button onClick={onClose} style={styles.closeButton} aria-label="닫기">
            ✕
          </button>
        </div>

        <div style={styles.customerInfo}>
          <div style={styles.customerRow}>
            <div style={styles.customerItem}>
              <span style={styles.customerLabel}>회사명</span>
              <span style={styles.customerValue}>{formData.companyName}</span>
            </div>
            <div style={styles.customerItem}>
              <span style={styles.customerLabel}>담당자</span>
              <span style={styles.customerValue}>{formData.manager}</span>
            </div>
          </div>
          <div style={styles.customerRow}>
            <div style={styles.customerItem}>
              <span style={styles.customerLabel}>연락처</span>
              <span style={styles.customerValue}>{formData.mobile || formData.phone}</span>
            </div>
            <div style={styles.customerItem}>
              <span style={styles.customerLabel}>주소</span>
              <span style={styles.customerValue}>{formData.address}</span>
            </div>
          </div>
        </div>

        <div style={styles.tabs}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {})
              }}
            >
              <span style={styles.tabIcon}>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div style={styles.content}>
          {activeTab === 'info' && (
            <InfoTab
              formData={formData}
              handleInputChange={handleInputChange}
              usedMaterials={usedMaterials}
              totalMaterialCost={totalMaterialCost}
              materialCategory={materialCategory}
              handleCategoryChange={handleCategoryChange}
              materialSearchTerm={materialSearchTerm}
              handleMaterialSearchChange={handleMaterialSearchChange}
              handleMaterialKeyDown={handleMaterialKeyDown}
              materialSearchResults={materialSearchResults}
              selectedMaterialIndex={selectedMaterialIndex}
              handleSelectMaterial={handleSelectMaterial}
              materialQuantity={materialQuantity}
              setMaterialQuantity={setMaterialQuantity}
              handleDeleteMaterial={handleDeleteMaterial}
              customMaterialCost={customMaterialCost}
              setCustomMaterialCost={setCustomMaterialCost}
            />
          )}

          {activeTab === 'payment' && (
            <PaymentTab
              formData={formData}
              setFormData={setFormData}
              payments={payments}
              newPayment={newPayment}
              setNewPayment={setNewPayment}
              isAddingPayment={isAddingPayment}
              handleAddPayment={handleAddPayment}
              handleDeletePayment={handleDeletePayment}
              effectiveMaterialCost={effectiveMaterialCost}
              laborCost={formData.laborCost}
              history={history}
            />
          )}

          {activeTab === 'customer' && (
            <CustomerTab
              formData={formData}
              setFormData={setFormData}
              token={token}
            />
          )}

          {activeTab === 'history' && (
            <HistoryTab history={history} />
          )}
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelButton}>
            취소
          </button>
          <button 
            onClick={handleSave} 
            style={{
              ...styles.saveButton,
              ...(isSaving ? styles.saveButtonDisabled : {})
            }}
            disabled={isSaving}
          >
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditRecordModal;
